import { FC, useRef, useMemo, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { RequirementItem } from "@/types/requirement";
import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import { productList, ProductListObjParams, ProductListParams, productSAdd } from "@/apis/product";
import { Flex, Modal, notification, Progress, Spin } from "antd";
import { Product } from "@/types/product";

export interface MatchProductRef {
  getMatchedProducts: () => Product[];
}

interface MatchProductProps {
  items: Array<RequirementItem>;
  supplierApi: string;
  setMatchErr?: (val: boolean) => void;
  errCodes?: Array<string>;
}

const MatchProduct = forwardRef<MatchProductRef, MatchProductProps>(({ items, supplierApi, setMatchErr, errCodes }, ref) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [processNum, setProcessNum] = useState<number>(0);
  const [totalToAdd, setTotalToAdd] = useState<number>(0);
  // 存储已匹配的产品（包括从接口获取的 + 新增的）
  const [matchedProducts, setMatchedProducts] = useState<Product[]>([]);

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    getMatchedProducts: () => matchedProducts,
  }), [matchedProducts]);

  // 未匹配的产品（用于显示提示）
  const unmatchedProducts = useMemo(() => {
    const productCodeMap = new Map(matchedProducts.map(p => [p.code, p]));
    const unmatched = items.filter(item => !productCodeMap.has(item.product?.code ?? "")).map(item => item.product);
    return unmatched as Product[];
  }, [items, matchedProducts]);

  useEffect(() => {
    if (items.length > 0 && supplierApi) {
      fetchProductsAndMatch();
    }
  }, [items, supplierApi]);

  const fetchProductsAndMatch = async () => {
    if (!!!supplierApi) {
      return notification.error({ message: t("requirement.supplierApiError"), placement: "top" });
    }
    const codes = items.map(item => item.product?.code).filter(Boolean) as string[];
    if (codes.length === 0) return;

    setLoading(true);
    setMatchedProducts([]); // 清空之前的数据
    try {
      const params: ProductListObjParams = {
        categoryId: undefined,
        params: {
          strs: codes,
        },
        wmsApi: supplierApi
      };
      const { body: apiProducts } = await productList(params);

      // 按照原始 items 顺序构建匹配结果
      const matched: Product[] = [];
      const toAdd: Product[] = [];

      for (const item of items) {
        const product = item.product;
        if (!product) continue;

        const existingProduct = apiProducts.find(p => p.code === product.code);
        if (existingProduct) {
          // 接口中存在该产品，直接存储
          matched.push(existingProduct);
        } else {
          // 接口中不存在，需要新增
          toAdd.push(product);
        }
      }

      // 先存储已匹配的产品
      setMatchedProducts(matched);

      // 如果有需要新增的产品，自动添加
      if (toAdd.length > 0) {
        setTotalToAdd(toAdd.length);
        // await autoAddProducts(toAdd, matched);
        //不自动匹配添加商品，提示手动添加
        notification.error({
          message: t('requirement.swapEnd'),
          description: (
            <>
              <div style={{ color: "red" }}>
                {t('requirement.noProducts')}
              </div>
              <div>{t('requirement.plAddProdictOne')}</div>
              <div>{t('requirement.plAddProdictTwo')}</div>
            </>
          )
        })
        setMatchErr?.(true);
      } else setMatchErr?.(false)
    } catch (error) {
      console.error('Failed to fetch products:', error);
      notification.error({
        message: t("requirement.productMatchFailed"),
        description: `${t("requirement.productMatchFailedDesc")}:${supplierApi}`,
        placement: "top"
      })
    } finally {
      setLoading(false);
    }
  };

  const autoAddProducts = async (toAdd: Product[], baseMatched: Product[]) => {
    setAutoMatching(true);
    const addProducts: Product[] = [];
    const failedProducts: Product[] = [];

    for (let i = 0; i < toAdd.length; i++) {
      const product = toAdd[i];
      if (!product) continue;
      try {
        const addedProduct = await productSAdd({
          code: product.code,
          name: product.name,
          model: product.model,
          desc: product.desc,
          uom: product.uom,
          process: product.process,
          cbm: product.cbm,
          t1: product.t1,
          hsCode: product.hsCode,
          deprecated: null,
        }, supplierApi);
        addProducts.push(addedProduct);
      } catch (error) {
        console.error(`Failed to add product ${product.code}:`, error);
        failedProducts.push(product);
      }
      setProcessNum(i + 1);
    }

    // 将新增成功的产品合并到匹配结果中，保持原有顺序
    if (addProducts.length > 0) {
      const finalMatched = [...baseMatched];
      // 按照原始 items 顺序添加新增的产品
      for (const item of items) {
        const product = item.product;
        if (!product) continue;
        const addedProduct = addProducts.find(p => p.code === product.code);
        if (addedProduct && !finalMatched.find(p => p.code === product.code)) {
          finalMatched.push(addedProduct);
        }
      }
      setMatchedProducts(finalMatched);
    }

    if (failedProducts.length > 0) {
      notification.warning({
        message: `${t("requirement.successAddProduct")} ${addProducts.length} ${t("requirement.productAddFailed")} ${failedProducts.length} 个`,
        description: failedProducts.map(p => p.code).join(", "),
        placement: "top"
      });
    } else if (addProducts.length > 0) {
      notification.success({
        message: `${t("requirement.successAddProduct")} ${addProducts.length} ${t("requirement.productAddFailed")}`,
        placement: "top"
      });
    }
    setAutoMatching(false);
  };

  const headerSub = (
    <Flex style={{ width: "100%" }}>
      {unmatchedProducts.length > 0 && <span style={{ color: "red" }}>{t("requirement.productsWithoutPrice")}</span>}
    </Flex>
  )

  const columns: ColumnsType<RequirementItem> = useMemo(() => [
    {
      title: t("product.code"),
      dataIndex: ['product', 'code'],
      key: 'productCode',
      width: 120,
      render: (_, item) => {
        const code = item.product?.code ?? "";
        const isMatched = matchedProducts.some(p => p.code === code);
        const isErr = !!errCodes && errCodes.includes(code)
        return <SText text={code} style={{ width: "100%", color: isMatched && !isErr ? undefined : "red" }} />;
      },
    },
    {
      title: t("product.name"),
      dataIndex: ['product', 'name'],
      key: 'productName',
      width: 150,
      render: (_, item) => <SText text={item.product?.name ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.model"),
      dataIndex: ['product', 'model'],
      key: 'productModel',
      width: 120,
      render: (_, item) => <SText text={item.product?.model ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("requirement.requireCount"),
      dataIndex: 'useAmount',
      width: 100,
      render: (_, item) => <SText text={item.useAmount ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("requirement.requirmentQuantity"),
      dataIndex: 'purCount',
      width: 100,
      render: (_, item) => <SText text={item.purCount ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.uom"),
      dataIndex: ['product', 'uom'],
      key: 'uom',
      width: 80,
      render: (_, item) => <SText text={item.product?.uom ?? ""} style={{ width: "100%" }} />,
    },
  ], [t, matchedProducts, errCodes]);

  return (
    <>
      <STableLayout
        loading={loading}
        dataSource={items}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={"max-content"}
        headerChildren={null}
        headerSub={headerSub}
        tableLayoutRef={tableRef}
        total={items.length}
        handleChange={() => { }}
        pagination={false}
        extHeight={70}
      />
      <Modal
        title={t('requirement.addingProduct') + "..."}
        closable={false}
        footer={null}
        maskClosable={false}
        open={autoMatching}
        centered
        width={1000}
      >
        <Flex style={{ marginTop: 120, marginBottom: 100 }} justify="center">
          <Spin spinning={autoMatching} size="large" />
        </Flex>
        <Progress percent={Number(((processNum / (totalToAdd || 1)) * 100).toFixed(2))} />
        <span>{`${t("requirement.processed")}${processNum}/${totalToAdd || 0}`}</span>
      </Modal>
    </>
  );
});

MatchProduct.displayName = 'MatchProduct';

export default MatchProduct;
